/* External */
import { useEffect } from 'react';

/* Utils */
import { noOp } from './functions';
import platform from './platform';
import { Direction } from './spatialNavigation';

const keyDownListeners = [];
const keyUpListeners = [];
const customBackListeners = [];
const BACK_DOOR_KEYS_MAP = {
  b: platform.keyCodes.back,
  p: platform.keyCodes.mediaControl.play,
  o: platform.keyCodes.mediaControl.pause,
  s: platform.keyCodes.mediaControl.stop,
  g: platform.keyCodes.mediaControl.playPause,
  n: platform.keyCodes.mediaControl.rewind,
  m: platform.keyCodes.mediaControl.fastForward,
};

const BACK_DOOR_KEY_CODES_MAP = {
  66: platform.keyCodes.back,
};

const KEY_SEQUENCE_TIMEOUT_MS = 1000;
const LONG_PRESS_DEFAULT_DELAY_MS = 800;
const keySequenceListeners = [];
let currentKeySequence = [];
let keySequenceTimeout;

function launchListener(event, keyCode, listenersArr) {
  if (!keyCode) return;

  listenersArr.some((keyListener) => {
    if (!keyListener) {
      return false;
    }
    const { listener, forKeyCodes } = keyListener;
    if (forKeyCodes && (forKeyCodes.length === 0 || forKeyCodes.includes(keyCode))) {
      const shouldPropagate = listener(event);
      return !shouldPropagate;
    }
    return false;
  });

  // Prevent default action for back key
  if (keyCode === platform.keyCodes.back) {
    event.preventDefault();
  }
}

function keySequenceListener() {
  for (const { listener, sequence } of keySequenceListeners) {
    if (currentKeySequence.length !== sequence.length) continue;
    if (currentKeySequence.some((element, index) => element !== sequence[index])) continue;
    listener();
  }
}

document.addEventListener('keydown', (event) => {
  const {
    key, keyCode, ctrlKey, altKey,
  } = event;

  if (ctrlKey && altKey) {
    const backDoorKeyCode = BACK_DOOR_KEYS_MAP[key.toLowerCase()]
      || BACK_DOOR_KEY_CODES_MAP[keyCode];

    if (backDoorKeyCode) {
      simulateKeyEvent(backDoorKeyCode, 'simulated');
      return;
    }
  }

  if (keySequenceTimeout) clearTimeout(keySequenceTimeout);
  currentKeySequence.push(keyCode);
  keySequenceListener();
  keySequenceTimeout = setTimeout(() => {
    currentKeySequence = [];
    keySequenceTimeout = undefined;
  }, KEY_SEQUENCE_TIMEOUT_MS);

  launchListener(event, keyCode, keyDownListeners);
}, true);

document.addEventListener('keyup', (event) => {
  const {
    key, keyCode, ctrlKey, altKey,
  } = event;

  if (ctrlKey && altKey) {
    const backDoorKeyCode = BACK_DOOR_KEYS_MAP[key.toLowerCase()]
      || BACK_DOOR_KEY_CODES_MAP[keyCode];

    if (backDoorKeyCode) {
      simulateKeyEvent(backDoorKeyCode, 'simulated', 'up');
      return;
    }
  }

  launchListener(event, keyCode, keyUpListeners);
}, true);

document.addEventListener('customback', (event) => {
  launchListener(event, platform.keyCodes.back, customBackListeners);
});

function addKeyEventListener(listenersArr) {
  return ({
    listener,
    forKeyCodes,
    priority = 0,
  }) => {
    if (!listener) return noOp;
    const keyCodesToUse = forKeyCodes && (Array.isArray(forKeyCodes) ? forKeyCodes : [forKeyCodes]);
    const listenerObject = { listener, forKeyCodes: keyCodesToUse, priority };
    let index = 0;
    while (index < listenersArr.length && listenersArr[index].priority > priority) {
      index += 1;
    }
    if (keyCodesToUse.includes(platform.keyCodes.back)) {
      customBackListeners.splice(index, 0, listenerObject);
    }
    listenersArr.splice(index, 0, listenerObject);
    return () => {
      listenersArr.splice(listenersArr.indexOf(listenerObject), 1);
      if (keyCodesToUse.includes(platform.keyCodes.back)) {
        customBackListeners.splice(customBackListeners.indexOf(listenerObject), 1);
      }
    };
  };
}

export const addKeyDownEventListener = addKeyEventListener(keyDownListeners);
export const addKeyUpEventListener = addKeyEventListener(keyUpListeners);

function simulateKeyEvent(keyCode, key, type = 'down') {
  const eventName = `key${type}`;
  const event = document.createEvent('HTMLEvents');
  event.initEvent(eventName, true, false);
  event.keyCode = keyCode;
  event.key = key;
  document.activeElement.dispatchEvent(event);
}

export function addLongPressEventSimulator({
  shortPressListener,
  forKeyCodes,
  simulatedKeyCode,
  simulatedKeyName,
  delay = LONG_PRESS_DEFAULT_DELAY_MS,
}) {
  let timeout;
  let isPressed = false;
  let isLongPressActive = false;

  const onKeyDown = () => {
    if (isPressed) return;
    isPressed = true;
    timeout = setTimeout(() => {
      simulateKeyEvent(simulatedKeyCode, simulatedKeyName);
      isLongPressActive = true;
      timeout = undefined;
    }, delay);
  };

  const onKeyUp = (event) => {
    if (!isLongPressActive) shortPressListener(event);

    isLongPressActive = false;
    isPressed = false;
    if (timeout) clearTimeout(timeout);
  };

  const removeKeyDownEventListener = addKeyDownEventListener({ listener: onKeyDown, forKeyCodes });
  const removeKeyUpEventListener = addKeyUpEventListener({ listener: onKeyUp, forKeyCodes });

  return () => {
    removeKeyDownEventListener();
    removeKeyUpEventListener();
  };
}

export function useOnKeyDown({
  listener,
  forKeyCodes,
  when = true,
  priority = 0,
}) {
  useEffect(() => {
    if (!when) return noOp;
    return addKeyDownEventListener({
      listener,
      forKeyCodes,
      priority,
    });
  }, [forKeyCodes, listener, priority, when]);
}

export function useOnKeyUp({
  listener,
  forKeyCodes,
  when = true,
  priority = 0,
}) {
  useEffect(() => {
    if (!when) return noOp;
    return addKeyUpEventListener({
      listener,
      forKeyCodes,
      priority,
    });
  }, [forKeyCodes, listener, priority, when]);
}

export function useOnKeySequence({
  listener,
  sequence,
  when = true,
}) {
  useEffect(() => {
    if (!when) return noOp;
    const listenerObj = { listener, sequence };
    keySequenceListeners.push(listenerObj);
    return () => {
      keySequenceListeners.splice(keySequenceListeners.indexOf(listenerObj), 1);
    };
  }, [sequence, listener, when]);
}

const backButtonKeys = [platform.keyCodes.back];
// Added escape(esc) to test on web view
if (import.meta.env.DEV) {
  backButtonKeys.push(27);
}

export function useOnBackPress({ listener, when, priority }) {
  useOnKeyDown({
    listener,
    forKeyCodes: backButtonKeys,
    when,
    priority,
  });
}

export function useOnLongPress({
  listener,
  forKeyCodes,
  simulatedKeyCode,
  simulatedKeyName,
  delay = LONG_PRESS_DEFAULT_DELAY_MS,
  when = true,
}) {
  useEffect(() => (
    when
      ? addLongPressEventSimulator({
        shortPressListener: listener,
        forKeyCodes,
        simulatedKeyCode,
        simulatedKeyName,
        delay,
      })
      : noOp
  ), [
    delay,
    forKeyCodes,
    listener,
    simulatedKeyCode,
    simulatedKeyName,
    when,
  ]);
}

export const KeyCodesToKeyName = {
  [platform.keyCodes.navigation.left]: 'ArrowLeft',
  [platform.keyCodes.navigation.right]: 'ArrowRight',
  [platform.keyCodes.navigation.enter]: 'Enter',
};

export const KeyCodesToDirection = {
  [platform.keyCodes.navigation.left]: Direction.LEFT,
  [platform.keyCodes.navigation.right]: Direction.RIGHT,
  [platform.keyCodes.navigation.up]: Direction.UP,
  [platform.keyCodes.navigation.down]: Direction.DOWN,
};
